/**
 * Orders from the website, waiting for somebody at the till to take them.
 *
 * WHAT THIS IS FOR
 * ----------------
 * A customer who orders online has asked, not ordered: nothing is cooked until
 * a cashier says yes. Until then they are looking at a page that says "waiting
 * for Food Run to confirm", so an order nobody notices is a customer left
 * staring at a screen. This keeps saying so until somebody answers: a bar
 * across the top of the till and a chime every few seconds, for as long as
 * anything is waiting. It does not stop by itself and nothing cancels an order
 * for being slow — that was the restaurant's decision.
 *
 * It shows on every screen a cashier can be on, not only the till, because the
 * one moment an order arrives is exactly the moment they are somewhere else.
 *
 * Accepting is taking the order: the server hands it to the same code as an
 * order keyed in here, so it lands in the kitchen, on this cashier's shift,
 * as an ordinary delivery or takeaway. The delivery charge comes worked out
 * from the distance and can be changed before accepting.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { useAuth } from '../../stores/auth.js';
import { Button } from '../../components/ui/Button.jsx';
import { playNewOrderChime, unlockOnFirstGesture } from '../kitchen/chime.js';

/** Often: a customer is watching a spinner for as long as this takes. */
const POLL_MS = 5_000;
/** How often the till says it again while anything waits. */
const CHIME_MS = 6_000;

const DECLINE_REASONS = ['Too busy right now', 'An item is not available', 'We cannot deliver there', 'Closing soon'];

function waitedFor(seconds) {
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min ago`;
}

function Request({ order, onDone }) {
  const isDelivery = order.fulfilment === 'delivery';
  const [fee, setFee] = useState(String(order.deliveryFee ?? 0));
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');

  const feeNumber = Number(fee);
  const feeOk = !isDelivery || (fee.trim() !== '' && Number.isFinite(feeNumber) && feeNumber >= 0);
  const total = order.total - (order.deliveryFee ?? 0) + (isDelivery && feeOk ? feeNumber : 0);

  const accept = useMutation({
    mutationFn: () => api.post(`/online-orders/${order.id}/accept`, isDelivery ? { deliveryFee: feeNumber } : {}),
    onSuccess: onDone,
  });
  const decline = useMutation({
    mutationFn: () => api.post(`/online-orders/${order.id}/decline`, { reason: reason.trim() }),
    onSuccess: onDone,
  });
  const busy = accept.isPending || decline.isPending;
  const error = accept.error ?? decline.error;

  return (
    <article className="rounded-xl border border-rule bg-surface-2 p-4" aria-label={`Online order ${order.reference}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${isDelivery ? 'bg-brand-400/15 text-brand-300' : 'bg-surface-3 text-ink-2'}`}>
              {isDelivery ? 'Delivery' : 'Pickup'}
            </span>
            <span className="text-xs text-ink-3">
              {order.reference} · {waitedFor(order.waitingSeconds)}
            </span>
          </div>
          <p className="mt-1.5 font-semibold text-ink">{order.customerName}</p>
          <p className="flex flex-wrap items-center gap-2 text-sm text-ink-2">
            <a href={`tel:${order.customerPhone}`} className="font-medium text-brand-300 underline-offset-2 hover:underline">
              {order.customerPhone}
            </a>
            {/* The number was typed and never proved: ring it before accepting. */}
            {order.phoneVerified === false && (
              <span className="rounded-full bg-warn-bg px-2 py-0.5 text-xs font-semibold text-warn">Number not verified · call first</span>
            )}
          </p>
        </div>
        <p className="shrink-0 font-semibold text-lg text-ink">{formatPKR(total)}</p>
      </div>

      {isDelivery && (
        <p className="mt-2 text-sm text-ink-2">
          {order.addressLabel ? `${order.addressLabel} · ` : ''}
          {order.address}
          {order.distanceKm != null ? ` · ${order.distanceKm} km` : ''}
        </p>
      )}

      <ul className="mt-3 space-y-1.5 border-t border-rule pt-3 text-sm">
        {order.items.map((line, at) => (
          <li key={at} className="flex gap-2">
            <span className="w-7 shrink-0 font-semibold text-brand-300">{line.qty}×</span>
            <span className="min-w-0 flex-1">
              <span className="text-ink">{line.name}</span>
              {line.choices?.length > 0 && <span className="block text-xs text-ink-3">{line.choices.join(' · ')}</span>}
            </span>
            <span className="shrink-0 text-ink-2">{formatPKR(line.total)}</span>
          </li>
        ))}
      </ul>

      {order.note && (
        <p className="mt-3 rounded-lg bg-surface-3 px-3 py-2 text-sm text-ink">
          <span className="text-ink-3">Kitchen note: </span>
          {order.note}
        </p>
      )}

      {isDelivery && (
        <label className="mt-3 flex items-center justify-between gap-3 text-sm text-ink-2">
          <span>
            Delivery charge
            <span className="block text-xs text-ink-3">Worked out from the distance. Change it if you need to.</span>
          </span>
          <input
            value={fee}
            onChange={(e) => setFee(e.target.value)}
            inputMode="numeric"
            aria-label={`Delivery charge for ${order.reference}`}
            className="h-10 w-28 rounded-lg border border-rule bg-surface-3 px-3 text-right text-sm text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          />
        </label>
      )}

      {error && (
        <p role="alert" className="mt-3 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">
          {error.message}
        </p>
      )}

      {declining ? (
        <div className="mt-3 border-t border-rule pt-3">
          <span className="mb-1.5 block text-sm font-medium text-ink-2">Why? The customer is shown this.</span>
          <div className="flex flex-wrap gap-1.5">
            {DECLINE_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(r)}
                aria-pressed={reason === r}
                className={`rounded-lg border px-2.5 py-1.5 text-sm ${reason === r ? 'border-brand-400 bg-brand-400/15 text-brand-300' : 'border-rule text-ink-2 hover:text-ink'}`}
              >
                {r}
              </button>
            ))}
          </div>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="or type a reason"
            aria-label={`Reason for declining ${order.reference}`}
            className="mt-2.5 h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          />
          <div className="mt-3 flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={() => setDeclining(false)} disabled={busy}>
              Back
            </Button>
            <Button className="flex-1 !bg-bad hover:!brightness-110" busy={decline.isPending} disabled={reason.trim().length < 3 || busy} onClick={() => decline.mutate()}>
              Decline the order
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex gap-2">
          <Button variant="secondary" onClick={() => setDeclining(true)} disabled={busy}>
            Decline
          </Button>
          <Button className="flex-1" busy={accept.isPending} disabled={!feeOk || busy} onClick={() => accept.mutate()}>
            Accept · send to kitchen
          </Button>
        </div>
      )}
    </article>
  );
}

export function OnlineOrders() {
  const can = useAuth((s) => s.can);
  const allowed = can('pos.create.pos');
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const waiting = useQuery({
    queryKey: ['online-orders', 'waiting'],
    queryFn: () => api.get('/online-orders/waiting'),
    refetchInterval: POLL_MS,
    enabled: allowed,
  });
  const orders = waiting.data?.orders ?? [];
  const anyWaiting = allowed && orders.length > 0;

  // A browser plays nothing until somebody has touched the page.
  useEffect(() => unlockOnFirstGesture(), []);

  // Say so now, and keep saying so while anything is waiting.
  useEffect(() => {
    if (!anyWaiting) return undefined;
    playNewOrderChime();
    const timer = setInterval(playNewOrderChime, CHIME_MS);
    return () => clearInterval(timer);
  }, [anyWaiting]);

  if (!anyWaiting) return null;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['online-orders', 'waiting'] });
    // An accepted order is now an open order on the till.
    queryClient.invalidateQueries({ queryKey: ['pos', 'open-orders'] });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed left-1/2 top-2 z-40 flex -translate-x-1/2 animate-pulse items-center gap-2 rounded-full bg-brand-400 px-4 py-2 text-sm font-semibold text-black shadow-lg"
      >
        <span className="size-2 rounded-full bg-black" aria-hidden="true" />
        {orders.length === 1 ? '1 online order waiting' : `${orders.length} online orders waiting`}
        <span className="rounded-full bg-black/15 px-2 py-0.5 text-xs">View</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Online orders waiting">
          <div className="my-6 w-full max-w-lg rounded-xl border border-rule bg-surface p-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-semibold text-lg text-ink">Online orders waiting</h2>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Close
              </Button>
            </div>
            <p className="mt-1 text-xs text-ink-3">The customer is waiting for an answer. Accepting sends the order to the kitchen.</p>
            <div className="mt-4 space-y-3">
              {orders.map((order) => (
                <Request key={order.id} order={order} onDone={refresh} />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
